import { useMutation, useQuery } from '@apollo/client'
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material'
import {
    type GetFilteredRostersWithCandidateQuery,
    type GetProcessStatusesQuery,
} from '__generated__/graphql'
import { useState } from 'react'
import { minimalInputSx } from 'recruiter/components/RosterTableToolbar/styles'
import {
    GetFilteredRostersWithCandidate,
    GetRosterSearchV2,
} from 'recruiter/contexts'
import { filterLabelSx } from 'recruiter/filterStyles'
import { UpdateProcessStatus } from 'recruiter/graphql/updateProcessStatus.gql'
import ProcessStatusTimeline from 'shared/components/ProcessStatusTimeline'
import useToast from 'shared/components/Toast/useToast'
import { GetProcessStatuses } from 'shared/graphql/candidateProcessStatus.gql'
import {
    formatGraphQLError,
    sortProcessStatusesBySummaryGroup,
} from 'shared/utils'

export default function RosterPanelProcessStatus({
    rosterId,
    history,
    rosterCandidateId,
    canEdit,
}: {
    rosterId: string
    history: GetFilteredRostersWithCandidateQuery['rosterSearch']['rosters'][number]['rosterCandidate']['candidateProcessStatusHistory']
    rosterCandidateId: string
    canEdit: boolean
}) {
    const [processStatusOptions, setProcessStatusOptions] = useState<
        GetProcessStatusesQuery['processStatuses']
    >([])
    const [selectedProcessStatus, setSelectedProcessStatus] = useState<
        GetProcessStatusesQuery['processStatuses'][number] | null
    >(null)
    const toast = useToast()

    const { loading: areProcessStatusesLoading } = useQuery(
        GetProcessStatuses,
        {
            onCompleted: (data) => {
                setProcessStatusOptions(
                    sortProcessStatusesBySummaryGroup(data.processStatuses)
                )
            },
        }
    )

    const [updateCandidateProcessStatus] = useMutation(UpdateProcessStatus, {
        refetchQueries: [
            GetFilteredRostersWithCandidate,
            GetRosterSearchV2,
            'GetRosterCandidatePanelData',
        ],
        onCompleted({ updateCandidateProcessStatus: { success, message } }) {
            if (success) {
                toast({
                    title: 'Success',
                    message: message || 'Process status updated successfully',
                    severity: 'success',
                })
            } else {
                toast({
                    title: 'Error',
                    message: message || 'Error updating process status',
                    severity: 'error',
                })
            }
        },
        onError(error) {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error updating process status'
                ),
                severity: 'error',
            })
        },
    })

    const handleSave = () => {
        updateCandidateProcessStatus({
            variables: {
                rosterId,
                rosterCandidateIds: [rosterCandidateId],
                processStatus: selectedProcessStatus!.status,
            },
        })
        setSelectedProcessStatus(null)
    }

    return (
        <Box>
            <Typography sx={filterLabelSx}>Status</Typography>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1,
                }}
            >
                <Autocomplete
                    disabled={!canEdit}
                    loading={areProcessStatusesLoading}
                    disableClearable={Boolean(selectedProcessStatus)}
                    options={processStatusOptions}
                    groupBy={(option) =>
                        option.processStatusSummaryGroup?.label || ''
                    }
                    value={selectedProcessStatus}
                    getOptionLabel={(option) => option.statusLabel}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search Statuses..."
                            size="small"
                        />
                    )}
                    renderOption={({ key, ...optionProps }, option) => (
                        <Box
                            component="li"
                            key={key}
                            {...optionProps}
                            sx={{
                                fontSize: '0.75rem',
                                py: 0.5,
                                minHeight: 'unset',
                            }}
                        >
                            {option.statusLabel}
                        </Box>
                    )}
                    renderGroup={(params) => (
                        <li key={params.key}>
                            <Box
                                sx={{
                                    fontSize: '0.6875rem',
                                    fontWeight: 600,
                                    py: 0.25,
                                    pl: 1,
                                    letterSpacing: '0.05em',
                                    bgcolor: 'grey.50',
                                }}
                            >
                                {params.group}
                            </Box>
                            <ul style={{ padding: 0 }}>{params.children}</ul>
                        </li>
                    )}
                    sx={{ minWidth: 200, ...minimalInputSx }}
                    onChange={(_, value) => {
                        setSelectedProcessStatus(value)
                    }}
                />
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={!selectedProcessStatus || !canEdit}
                    sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                >
                    Save
                </Button>
            </Box>
            <ProcessStatusTimeline
                history={
                    history?.map((item) => ({
                        processStatus: item.processStatus?.statusLabel,
                        createdByUserPreferredName: `${item.createdByUser?.firstName} ${item.createdByUser?.lastName}`,
                        createdAt: item.createdAt,
                    })) ?? []
                }
            />
        </Box>
    )
}
